import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { fetchByFeature } from '../../../utils/TableHeads/FetchByFeature';

function CommonTableHead({ tableHeads, feature }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalCount, setTotalCount] = React.useState(0);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [dialogType, setDialogType] = React.useState(null); // 'view', 'edit', 'delete'
  const [editData, setEditData] = React.useState({});

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);

    if (feature) {
      fetchByFeature(feature, page, rowsPerPage)
        .then((data) => {
          if (!mounted) return;
          setRows(Array.isArray(data.items) ? data.items : []);
          setTotalCount(data.total || 0);
        })
        .catch((err) => {
          console.error('Error fetching rows for feature', feature, err);
          if (mounted) {
            setRows([]);
            setTotalCount(0);
          }
        })
        .finally(() => {
          if (mounted) setLoading(false);
        });
    } else {
      setRows([]);
      setTotalCount(0);
      setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [feature, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setDialogType('view');
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setEditData({ ...row });
    setDialogType('edit');
  };

  const handleDelete = (row) => {
    setSelectedRow(row);
    setDialogType('delete');
  };

  const handleCloseDialog = () => {
    setDialogType(null);
    setSelectedRow(null);
    setEditData({});
  };

  const handleSaveEdit = () => {
    // TODO: Implement API call to update the row
    console.log('Updating row:', editData);
    setRows(rows.map(r => r.id === editData.id ? editData : r));
    handleCloseDialog();
  };

  const handleConfirmDelete = () => {
    // TODO: Implement API call to delete the row
    console.log('Deleting row:', selectedRow);
    setRows(rows.filter(r => r.id !== selectedRow.id));
    handleCloseDialog();
  };

  const handleEditFieldChange = (key, value) => {
    setEditData({ ...editData, [key]: value });
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              {tableHeads.headers.map((head) => (
                <TableCell key={head} align="center">
                  {head}
                </TableCell>
              ))}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={tableHeads.headers.length + 1} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : rows.length ? (
              rows.map((row) => (
                <TableRow key={row.id || row.name}>
                  {tableHeads.dataSets.map((key) => (
                    <TableCell key={key} align="center">
                      {row[key] ?? '-'}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleView(row)}>
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEdit(row)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(row)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeads.headers.length + 1} align="center">
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* View Dialog */}
      <Dialog open={dialogType === 'view'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>View Record</DialogTitle>
        <DialogContent>
          {selectedRow && Object.entries(selectedRow).map(([key, value]) => (
            <div key={key} style={{ marginBottom: '10px' }}>
              <strong>{key}:</strong> {value}
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={dialogType === 'edit'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Record</DialogTitle>
        <DialogContent>
          {editData && tableHeads.dataSets.map((key) => (
            <div key={key} style={{ marginBottom: '15px' }}>
              <label>{key}: </label>
              <input
                type="text"
                value={editData[key] || ''}
                onChange={(e) => handleEditFieldChange(key, e.target.value)}
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              />
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={dialogType === 'delete'} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this record?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function PaginatedTable(props) {
  return (
    <CommonTableHead tableHeads={props.tableHeads} feature={props.feature} />
  );
}
